import React, { useEffect, useState } from "react";
import axios from "axios";
import { Copy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(value) {
  if (!value) return "date inconnue";
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function statusLabel(step, submittedAt) {
  const date = formatDate(submittedAt || step?.since);
  if (step?.state === "recu") return `reçu le ${date}`;
  if (step?.state === "lien_expire") return "lien expiré";
  return `lien envoyé le ${date}`;
}

export default function FormulairePanel({ sessionId, steps }) {
  const step = steps?.formulaire;
  const [data, setData] = useState(null);
  const [lienARecopier, setLienARecopier] = useState(null);

  useEffect(() => {
    if (step?.state === "sans_formulaire") return undefined;
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/formulaire/dossiers/${sessionId}`),
      axios.get(`${API}/session/${sessionId}`),
    ])
      .then(([dossierRes, sessionRes]) => {
        if (cancelled) return;
        setData({
          ...sessionRes.data,
          formUrl: `${window.location.origin}${dossierRes.data.form_path}`,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error.response?.data?.detail || "Impossible de charger le formulaire");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, step?.state]);

  if (step?.state === "sans_formulaire") {
    return (
      <p className="mx-auto max-w-2xl p-8 text-muted-foreground">
        Ce dossier a été créé par dépôt direct, sans formulaire client.
      </p>
    );
  }

  if (!data) {
    return <p className="p-8 text-muted-foreground">Chargement du formulaire...</p>;
  }

  const formulaire = data.formulaire || {};
  const photos = formulaire.photos_refs || [];
  const liensWeb = formulaire.liens_web || [];
  const mode = data.formulaire_mode || data.mode;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.formUrl);
      toast.success("Lien du formulaire copié");
    } catch (error) {
      setLienARecopier(data.formUrl);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-8">
      {step?.state === "lien_envoye" && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Lien du formulaire</p>
          <div className="flex gap-2">
            <Input readOnly value={data.formUrl} onFocus={(event) => event.target.select()} />
            <Button variant="outline" size="icon" title="Copier le lien du formulaire" onClick={handleCopy}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {step?.state === "lien_expire" && (
        <div className="space-y-2 text-muted-foreground">
          <p className="text-sm font-medium">Lien du formulaire</p>
          <Input readOnly disabled aria-disabled="true" value={data.formUrl} />
          <p className="text-sm">lien expiré — recréer un dossier</p>
        </div>
      )}

      <dl className="grid gap-4 sm:grid-cols-2">
        <div><dt className="text-sm text-muted-foreground">Formule</dt><dd>{data.tarif} €</dd></div>
        <div><dt className="text-sm text-muted-foreground">Mode</dt><dd>{mode === "light" ? "Allégé" : "Complet"}</dd></div>
        <div><dt className="text-sm text-muted-foreground">État</dt><dd>{statusLabel(step, formulaire.submitted_at)}</dd></div>
        <div><dt className="text-sm text-muted-foreground">Photos</dt><dd>{photos.length} photo{photos.length > 1 ? "s" : ""}</dd></div>
        {data.email_client && (
          <div><dt className="text-sm text-muted-foreground">Email</dt><dd>{data.email_client}</dd></div>
        )}
      </dl>

      {liensWeb.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Liens web</p>
          <ul className="space-y-1">
            {liensWeb.map((url) => (
              <li key={url}>
                <a className="text-sm text-primary underline" href={url} target="_blank" rel="noreferrer">
                  {url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={Boolean(lienARecopier)} onOpenChange={(open) => !open && setLienARecopier(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Lien du formulaire</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            La copie automatique a été refusée par le navigateur. Sélectionne le lien et copie-le à la main.
          </p>
          <Input readOnly value={lienARecopier || ""} onFocus={(event) => event.target.select()} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
